import { gql } from 'graphql-tag';
import { useMemo } from 'react';
import { useLingui } from '@lingui/react/macro';

import pick from 'lodash/pick';

import { BiQueryMode } from '@watershed/constants/bi';
import { Flags } from '@watershed/constants/flags';
import { PermissionType } from '@watershed/constants/permissions';
import ActivityIcon from '@watershed/icons/components/Activity';
import ChartIcon from '@watershed/icons/components/Chart';
import CleanPowerIcon from '@watershed/icons/components/CleanPower';
import EmissionFactorsIcon from '@watershed/icons/components/EmissionFactors';
import FactoryIcon from '@watershed/icons/components/Factory';
import FootprintsIcon from '@watershed/icons/components/Footprints';
import HomeIcon from '@watershed/icons/components/Home';
import KickoffIcon from '@watershed/icons/components/Kickoff';
import LabsIcon from '@watershed/icons/components/Labs';
import ReductionsIcon from '@watershed/icons/components/Reductions';
import ReportIcon from '@watershed/icons/components/Report';
import ReportDisclosureIcon from '@watershed/icons/components/ReportDisclosure';
import SetSquareIcon from '@watershed/icons/components/SetSquare';
import ShoppingBasketIcon from '@watershed/icons/components/ShoppingBasket';
import StackIcon from '@watershed/icons/components/Stack';
import isFetchingOrStale from '@watershed/shared-frontend/utils/isFetchingOrStale';
import { getStringifiedOrNull } from '@watershed/shared-universal/bi/hashUtils';
import { BiQueryComponentHashKey } from '@watershed/shared-universal/bi/types';
import {
  routeForActivityDataOverview,
  routeForBenchmarks,
  routeForCalculation,
  routeForEditReport,
  routeForEmissionsFactors,
  routeForFootprintBiDrilldown,
  routeForFootprintList,
  routeForFootprintOverview,
  routeForFootprintProductLevelEmissions,
  routeForFootprintSuppliers,
  routeForFormalReporting,
  routeForHome,
  routeForLabs,
  routeForMarketplace,
  routeForMeasurementProject,
  routeForMeasureProjects,
  routeForProductFootprintsMaterials,
  routeForReductions,
  routeForSavedViewsLandingPage,
  routeForTasks,
} from '@watershed/shared-universal/dashboardRoutes';
import { hasPermission } from '@watershed/shared-universal/permissions/permissionUtils';
import isNotNullish from '@watershed/shared-util/src/isNotNullish';

import {
  useActiveMeasurementProjectsQuery,
  useSidebarSavedViewsQuery,
} from '@watershed/app-dashboard/generated/urql';

import { useAllFeatureFlags } from '../../../utils/FeatureFlag';
import {
  hasAccessToBenchmarks,
  hasPermissionsForCalculationCustomization,
  hasPermissionsForMeasurementProjects,
  hasPermissionsForMethodologyCustomization,
  hasPermissionsForReductionPlans,
  hasPermissionsForSupplyChain,
} from '../../../utils/featureAccess';
import { useUserContext } from '../../../utils/UserContext';
import {
  isTaskCompleted,
  useGetEngagementTasksTypesAndStatus,
} from '../../footprint/suppliers/engagementTasks/useEngagementTasks';
import { useRecentReportSummaries } from '../../report/useRecentReports';
import type { NavSectionData, NavSectionItem, SideBarVariant } from '../utils';

gql`
  query ActiveMeasurementProjects @withOwner(owner: ApplicationPlatform) {
    measurementProjectsRelevantToViewer(filters: { active: true }, limit: 10) {
      id
      name
    }
  }
`;

gql`
  query SidebarSavedViews($input: BiSavedViewsInput!)
  @withOwner(owner: Calcprint) {
    biSavedViewsVersioned(input: $input) {
      id
      stableId
      name
    }
  }
`;

/**
 * Constructs our location object from a string route, assumes the route has no origin!
 */
export function makeNavItemLocationFromRoute(
  route: string
): NavSectionItem['location'] {
  return pick(new URL(window.location.origin + route), [
    'pathname',
    'search',
    'hash',
  ]);
}

export default function useSidebarStandardVariant(): SideBarVariant {
  // We have a lot of utility functions here, so just use the hook
  // to subscribe to locale changes, and use the t macro for actual strings.
  const { t } = useLingui();
  const userContext = useUserContext();
  const featureFlags = useAllFeatureFlags();
  const tasksNavItem = useTasksNavSection();
  const { recentReports, isLoading: isLoadingRecentReports } =
    useRecentReportSummaries();

  const [measurementProjectsResult] = useActiveMeasurementProjectsQuery();

  const projects = useMemo(() => {
    return (
      measurementProjectsResult.data?.measurementProjectsRelevantToViewer ?? []
    );
  }, [measurementProjectsResult]);

  const [savedViewsResult] = useSidebarSavedViewsQuery({
    variables: {
      input: {
        mode: BiQueryMode.Snapshot,
        limit: 10,
        prioritizeOwnerId: userContext.userId,
      },
    },
  });

  const savedViews = useMemo(() => {
    return savedViewsResult.data?.biSavedViewsVersioned ?? [];
  }, [savedViewsResult]);

  const navEntries = useMemo(() => {
    const { permissions } = userContext;

    const homeNavSection: NavSectionData = {
      subentries: [
        {
          icon: HomeIcon,
          name: t`Home`,
          hasPermission: hasPermission(
            permissions,
            [PermissionType.ViewFootprint],
            { allowAnyObject: true }
          ),
          location: makeNavItemLocationFromRoute(routeForHome()),
        },
        featureFlags.get(Flags.Labs)
          ? {
              icon: LabsIcon,
              name: t`Labs`,
              // hasPermission is always true because we only show
              // this if the feature flag is enabled and labs experiments
              // are individually permissioned.
              hasPermission: true,
              location: makeNavItemLocationFromRoute(routeForLabs()),
            }
          : null,
        featureFlags.get(Flags.SupplyChainHideNetworkTasksNavItem)
          ? null
          : tasksNavItem,
      ].filter(isNotNullish),
    };

    const measurementsNavSection: NavSectionData = {
      name: t`Measure`,
      subentries: [],
    };

    const projectsPlaceholderMessage = measurementProjectsResult.error
      ? t`There was an error loading your measurements.`
      : projects.length === 0
        ? t`No active measurements`
        : undefined;
    measurementsNavSection.subentries.push({
      name: t`Measurements`,
      icon: SetSquareIcon,
      location: makeNavItemLocationFromRoute(routeForMeasureProjects()),
      disabled: !userContext.watershedPlanIncludesMeasurement,
      hasPermission: hasPermission(
        permissions,
        [PermissionType.ManageDatasource, PermissionType.ApproveDatasource],
        { allowAnyObject: true }
      ),
      menu:
        projects.length > 0
          ? {
              name: t`Active`,
              loading: isFetchingOrStale(measurementProjectsResult),
              placeholderMessage: projectsPlaceholderMessage,
              items: projects
                .map((project) => ({
                  id: project.id,
                  name: project.name,
                  location: makeNavItemLocationFromRoute(
                    routeForMeasurementProject(project.id)
                  ),
                }))
                .concat([
                  {
                    id: 'all',
                    name: t`View all`,
                    location: makeNavItemLocationFromRoute(
                      routeForMeasureProjects()
                    ),
                  },
                ]),
            }
          : undefined,
    });
    measurementsNavSection.subentries.push({
      name: t`Activity data`,
      location: makeNavItemLocationFromRoute(routeForActivityDataOverview()),
      hasPermission: hasPermissionsForMeasurementProjects(userContext),
      icon: ActivityIcon,
    });

    if (featureFlags.get(Flags.CalculationMethodologyCustomizationPage)) {
      measurementsNavSection.subentries.push({
        name: t`Emissions factors`,
        location: makeNavItemLocationFromRoute(routeForEmissionsFactors()),
        hasPermission: hasPermissionsForMethodologyCustomization(userContext),
        icon: EmissionFactorsIcon,
      });
    }

    if (featureFlags.get(Flags.CalculationCustomization)) {
      measurementsNavSection.subentries.push({
        name: t`Calculation`,
        icon: EmissionFactorsIcon,
        location: makeNavItemLocationFromRoute(routeForCalculation()),
        hasPermission: hasPermissionsForCalculationCustomization(userContext),
      });
    }

    measurementsNavSection.subentries.push({
      icon: FootprintsIcon,
      name: t`Footprints`,
      location: makeNavItemLocationFromRoute(routeForFootprintList()),
      hasPermission: hasPermission(
        permissions,
        [PermissionType.ViewFootprint],
        { allowAnyObject: true }
      ),
    });

    const analyzeNavSection: NavSectionData = {
      name: t`Analyze`,
      subentries: [],
    };

    analyzeNavSection.subentries.push({
      icon: StackIcon,
      name: t`Overview`,
      location: makeNavItemLocationFromRoute(routeForFootprintOverview()),
      hasPermission: hasPermission(
        permissions,
        [PermissionType.ViewFootprint],
        { allowAnyObject: true }
      ),
    });
    analyzeNavSection.subentries.push({
      name: t`Drilldown`,
      location: makeNavItemLocationFromRoute(routeForFootprintBiDrilldown()),
      hasPermission: hasPermission(
        permissions,
        [PermissionType.ViewFootprint],
        { allowAnyObject: true }
      ),
      icon: ChartIcon,
      menu:
        savedViews.length > 0
          ? {
              name: t`Saved views`,
              loading: isFetchingOrStale(savedViewsResult),
              items: savedViews
                .map((view) => ({
                  id: view.stableId,
                  name: view.name,
                  location: makeNavItemLocationFromRoute(
                    routeForFootprintBiDrilldown(
                      {},
                      {
                        [BiQueryComponentHashKey.view]: getStringifiedOrNull(
                          view.stableId
                        ),
                      }
                    )
                  ),
                }))
                .concat([
                  {
                    id: 'view-all',
                    name: t`View all`,
                    location: makeNavItemLocationFromRoute(
                      routeForSavedViewsLandingPage()
                    ),
                  },
                ]),
            }
          : undefined,
    });
    if (featureFlags.get(Flags.FootprintProductLevelEmissions)) {
      analyzeNavSection.subentries.push({
        name: t`Products & materials`,
        location: makeNavItemLocationFromRoute(
          routeForFootprintProductLevelEmissions()
        ),
        hasPermission: hasPermission(permissions, [
          PermissionType.ViewFootprintDetail,
        ]),
        icon: ShoppingBasketIcon,
      });
    }
    if (
      featureFlags.get(Flags.ProductFootprintsScope3) &&
      featureFlags.get(Flags.CustomerAIEnabled)
    ) {
      analyzeNavSection.subentries.push({
        name: t`Product footprints`,
        location: makeNavItemLocationFromRoute(
          routeForProductFootprintsMaterials()
        ),
        hasPermission: hasPermission(permissions, [
          PermissionType.ViewProductFootprint,
        ]),
        icon: ShoppingBasketIcon,
      });
    }
    if (featureFlags.get(Flags.FootprintSuppliersPage)) {
      analyzeNavSection.subentries.push({
        name: t`Supply chain`,
        location: makeNavItemLocationFromRoute(routeForFootprintSuppliers()),
        hasPermission: hasPermissionsForSupplyChain(userContext),
        icon: FactoryIcon,
      });
    }
    analyzeNavSection.subentries.push({
      name: t`Benchmarks`,
      location: makeNavItemLocationFromRoute(routeForBenchmarks()),
      hasPermission: hasAccessToBenchmarks(userContext, featureFlags),
      icon: KickoffIcon,
    });

    const reportNavSection: NavSectionData = {
      name: t`Report`,
      subentries: [
        {
          icon: ReportIcon,
          name: t`Disclosures & reports`,
          location: makeNavItemLocationFromRoute(routeForFormalReporting()),
          disabled: !userContext.watershedPlanIncludesReporting,
          disabledTooltip: t`Your organization does not have a reporting plan`,
          hasPermission: hasPermission(
            permissions,
            [PermissionType.ViewReportQuestionInstance],
            { allowAnyObject: true }
          ),
          menu:
            !isLoadingRecentReports && recentReports.length > 0
              ? {
                  name: t`Latest`,
                  items: [
                    ...recentReports.map((report) => ({
                      id: report.id,
                      name: report.name,
                      location: makeNavItemLocationFromRoute(
                        routeForEditReport(report.id)
                      ),
                    })),
                    {
                      id: 'view-all',
                      name: t`View all`,
                      location: makeNavItemLocationFromRoute(
                        routeForFormalReporting({ page: 'reports' })
                      ),
                    },
                  ],
                }
              : undefined,
        },
      ],
    };

    const planNavSection: NavSectionData = {
      name: t`Plan`,
      subentries: [],
    } as const;
    planNavSection.subentries.push({
      name: t`Reduction plans`,
      location: makeNavItemLocationFromRoute(routeForReductions()),
      hasPermission: hasPermissionsForReductionPlans(userContext),
      icon: ReductionsIcon,
    });
    planNavSection.subentries.push({
      name: t`Marketplace`,
      location: makeNavItemLocationFromRoute(routeForMarketplace()),
      icon: CleanPowerIcon,
      disabled: false,
      hasPermission: hasPermission(permissions, [
        PermissionType.ManageMarketplacePurchases,
      ]),
    });

    const navSections: ReadonlyArray<NavSectionData | null> = [
      homeNavSection,
      measurementsNavSection,
      analyzeNavSection.subentries.length > 0 ? analyzeNavSection : null,
      reportNavSection,
      planNavSection,
    ];

    return navSections.filter(isNotNullish);
  }, [
    t,
    userContext,
    featureFlags,
    tasksNavItem,
    recentReports,
    isLoadingRecentReports,
    measurementProjectsResult,
    projects,
    savedViewsResult,
    savedViews,
  ]);

  return { navEntries };
}

function useTasksNavSection(): NavSectionItem | null {
  const { t } = useLingui();
  const userContext = useUserContext();
  const { engagementTasks } = useGetEngagementTasksTypesAndStatus();

  if (
    !hasPermission(userContext.permissions, [PermissionType.ManageDisclosures])
  ) {
    return null;
  }

  if (engagementTasks.length === 0) {
    return null;
  }

  const incompleteTasks = engagementTasks.filter(
    (task) => !isTaskCompleted(task)
  );

  return {
    name: t`Network tasks`,
    location: makeNavItemLocationFromRoute(routeForTasks()),
    hasPermission: hasPermission(userContext.permissions, [
      PermissionType.ManageDisclosures,
    ]),
    chip: incompleteTasks.length > 0 ? incompleteTasks.length.toString() : null,
    icon: ReportDisclosureIcon,
  };
}
